import { useEffect, useState } from 'react'
import {
  CombineNote,
  GroupedNotes,
  LeadNote,
  Note,
  OpportunityNote,
} from '@user/components/Notes/typings'
import { getLocationAddress } from '@user/store/entityLocations'
import { NoteDetailsProps } from '@user/components/Leads/LeadCard/NotesDrawer/typings'
import format from 'date-fns/format'
import { useD2DKnockEnabled } from '@user/store/tenantFeatures/hooks/useD2DKnockEnabled'
import { useMapViewFeatureEnabled } from '@user/store/tenantFeatures/hooks/useMapViewFeatureEnabled'

export const useCombinedNotes = (
  detail: NoteDetailsProps,
  notes: LeadNote[] | OpportunityNote[],
  showPestPacNote: boolean,
  showKnockStatusNote: boolean,
  sortBy: string,
  externalLinkId: string,
) => {
  const [combinedNotes, setCombinedNotes] = useState<CombineNote[]>([])
  const [groupedNotes, setGroupedNotes] = useState<GroupedNotes[]>([])
  const d2dKnockEnabled = useD2DKnockEnabled()
  const mapViewFeatureEnabled = useMapViewFeatureEnabled()

  const featuresOn = d2dKnockEnabled && mapViewFeatureEnabled

  useEffect(() => {
    let address = ''
    const updateCombinedNotes = () => {
      const serviceLocations = detail?.locations?.serviceLocations || []
      const updatedNotes: CombineNote[] = []

      if (notes.length > 0) {
        notes.forEach((note: Note) => {
          if (externalLinkId) {
            const locations = serviceLocations.filter(
              (item) =>
                item.externalLinkId === (note.externalLinkId || externalLinkId),
            )
            if (locations.length > 0) address = getLocationAddress(locations[0])
          } else {
            if (serviceLocations.length > 0)
              address = getLocationAddress(serviceLocations[0])
          }
          const createdByUser =
            note.createdBy?.isActive === false
              ? note.createdBy?.fullName + ' (Inactive)'
              : note.createdBy?.fullName
          updatedNotes.push({
            createdBy: createdByUser || note.externalUser || '',
            createdById: note.createdBy?.id || '',
            createdDate: note.createdDate,
            note: note.detail,
            id: Number(note.id),
            pestPac: Boolean(note.externalLinkId),
            noteExternalLinkId: note.externalLinkId,
            code: note.noteCode || '',
            address,
            rejectedBy: note.rejectedBy,
            rejectedDate: note.rejectedDate,
            title: note.title,
            updatedBy: note.updatedBy,
            knockStatusNote: note.knockStatusNote,
          })
        })
      }

      let sortedNotes =
        sortBy === 'NEW'
          ? updatedNotes.sort(
              (a, b) => b.createdDate.getTime() - a.createdDate.getTime(),
            )
          : updatedNotes.sort(
              (a, b) => a.createdDate.getTime() - b.createdDate.getTime(),
            )

      if (!showPestPacNote) {
        sortedNotes = sortedNotes.filter((note) => !note.pestPac)
      }

      if (!featuresOn || !showKnockStatusNote) {
        sortedNotes = sortedNotes.filter((note) => !note.knockStatusNote)
      }

      const groupedData = {}

      sortedNotes.forEach((item) => {
        const createdDate = format(item.createdDate, 'MMMM dd, yyyy')
        if (!groupedData[createdDate]) {
          groupedData[createdDate] = []
        }
        groupedData[createdDate].push(item)
      })

      const resultArray = Object.keys(groupedData).map((key) => ({
        createdDate: key,
        notes: groupedData[key],
      }))

      setGroupedNotes(resultArray)
      setCombinedNotes(sortedNotes)
    }

    updateCombinedNotes()
  }, [
    notes,
    showPestPacNote,
    showKnockStatusNote,
    sortBy,
    externalLinkId,
    detail?.locations?.serviceLocations,
  ])

  return { combinedNotes, groupedNotes }
}
